import React from "react";
import {
  Alert,
  Button,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Switch,
  TextField,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import useAccountSettings from "../hooks/useAccountSettings";
import MfaSetupModal from "./MfaSetupModal";

const TIMEZONE_OPTIONS = [
  { value: "PKT", label: "PKT - Pakistan Standard Time (GMT+05:00)" },
  { value: "GMT", label: "GMT - Greenwich Mean Time (GMT+00:00)" },
  { value: "UTC", label: "UTC - Coordinated Universal Time (GMT+00:00)" },
  { value: "EST", label: "EST - Eastern Standard Time (GMT-05:00)" },
  { value: "CST", label: "CST - Central Standard Time (GMT-06:00)" },
  { value: "MST", label: "MST - Mountain Standard Time (GMT-07:00)" },
  { value: "PST", label: "PST - Pacific Standard Time (GMT-08:00)" },
];

const fieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    "& fieldset": {
      borderColor: "#E2E8F0",
    },
    "&:hover fieldset": {
      borderColor: "#0A2342",
    },
  },
  "& .MuiInputLabel-root": {
    color: "#64748B",
    "&.Mui-focused": {
      color: "#0A2342",
    },
  },
  "& .MuiOutlinedInput-input": {
    padding: "14px",
    fontSize: "14px",
    color: "#0A2342",
  },
};

const disabledEmailFieldSx = {
  ...fieldSx,
  "& .MuiInputLabel-root.Mui-disabled": {
    color: "#334155",
  },
  "& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
    borderColor: "#94A3B8",
  },
  "& .MuiOutlinedInput-input.Mui-disabled": {
    WebkitTextFillColor: "#0F172A",
    color: "#0F172A",
    opacity: 1,
  },
};

const AccountSettings = () => {
  const {
    currentUser,
    loadError,
    loading,
    isSettingUpMfa,
    isVerifyingMfa,
    mfaSetup,
    oldPassword,
    newPassword,
    confirmPassword,
    timeZone,
    mfaEnabled,
    mfaCode,
    mfaVerificationError,
    mfaSetupStep,
    showOldPassword,
    showNewPassword,
    showConfirmPassword,
    snackbar,
    isMfaChanged,
    canSaveChanges,
    setOldPassword,
    setNewPassword,
    setConfirmPassword,
    setTimeZone,
    setShowOldPassword,
    setShowNewPassword,
    setShowConfirmPassword,
    setMfaSetupStep,
    handleMfaToggleChange,
    handleVerifyMfaCode,
    handleContinueMfaSetup,
    handleCloseMfaSetupFlow,
    handleMfaCodeChange,
    handleSaveChanges,
    handleCloseSnackbar,
  } = useAccountSettings();

  const handleCopySecret = async () => {
    const secret = mfaSetup?.secret;
    if (!secret || !navigator?.clipboard?.writeText) {
      return;
    }

    await navigator.clipboard.writeText(secret);
  };

  const isSetupModalOpen =
    mfaSetupStep === "success" ||
    (mfaEnabled && isMfaChanged && mfaSetupStep !== "done");

  return (
    <div className="mt-6">
      <div className="bg-white rounded-2xl border border-line max-sm:px-4 p-6">
        <div className="mb-6">
          <h2 className="text-lg font-bold text-primary">Account Settings</h2>
          <p className="text-sm text-mutedDark mt-1">
            Manage your account preferences and security
          </p>
        </div>

        {loadError && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {loadError}
          </Alert>
        )}

        <div className="space-y-6">
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={currentUser?.email || ""}
            placeholder="Logged in user email"
            variant="outlined"
            disabled
            sx={disabledEmailFieldSx}
          />

          <div className="space-y-4">
            <div>
            <h3 className="text-lg font-semibold text-primary">Change Password</h3>
            <p className="text-xs text-muted">Change your password</p>
            </div>

            <TextField
              fullWidth
              label="Current Password"
              type={showOldPassword ? "text" : "password"}
              value={oldPassword}
              onChange={(event) => setOldPassword(event.target.value)}
              placeholder="Enter current password"
              variant="outlined"
              focused
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowOldPassword((prev) => !prev)}
                      edge="end"
                      sx={{ color: "#64748B" }}
                    >
                      {showOldPassword ? (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />

            <TextField
              fullWidth
              label="New Password"
              type={showNewPassword ? "text" : "password"}
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder="Enter new password"
              variant="outlined"
              helperText="Minimum 8 characters"
              focused
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword((prev) => !prev)}
                      edge="end"
                      sx={{ color: "#64748B" }}
                    >
                      {showNewPassword ? (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />

            <TextField
              fullWidth
              label="Confirm New Password"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Confirm new password"
              variant="outlined"
              focused
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword((prev) => !prev)}
                      edge="end"
                      sx={{ color: "#64748B" }}
                    >
                      {showConfirmPassword ? (
                        <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                      ) : (
                        <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={fieldSx}
            />
          </div>

           <FormControl fullWidth>
            <InputLabel id="account-timezone-label">Time Zone</InputLabel>
            <Select
              labelId="account-timezone-label"
              value={timeZone}
              label="Time Zone"
              onChange={(event) => setTimeZone(event.target.value)}
              sx={{ borderRadius: "8px" }}
              renderValue={(selected) => {
                const selectedOption = TIMEZONE_OPTIONS.find(
                  (option) => option.value === selected,
                );
                return selectedOption ? selectedOption.label : selected;
              }}
            >
              {TIMEZONE_OPTIONS.map((zone) => (
                <MenuItem key={zone.value} value={zone.value}>
                  {zone.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-primary">
                  Multi-Factor Authentication (MFA)
                </h3>
                <p className="text-xs text-mutedLight mt-1">
                  Add an extra layer of security to your account
                </p>
              </div>
              <Switch
                checked={mfaEnabled}
                onChange={handleMfaToggleChange}
                disabled={isSettingUpMfa || isVerifyingMfa || loading}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#0A2342",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#0A2342",
                  },
                }}
              />
            </div>

            <MfaSetupModal
              open={isSetupModalOpen}
              onClose={handleCloseMfaSetupFlow}
              mfaSetupStep={mfaSetupStep}
              setMfaSetupStep={setMfaSetupStep}
              mfaSetup={mfaSetup}
              isSettingUpMfa={isSettingUpMfa}
              isVerifyingMfa={isVerifyingMfa}
              mfaCode={mfaCode}
              mfaVerificationError={mfaVerificationError}
              loading={loading}
              fieldSx={fieldSx}
              handleCopySecret={handleCopySecret}
              handleContinueMfaSetup={handleContinueMfaSetup}
              handleMfaCodeChange={handleMfaCodeChange}
              handleVerifyMfaCode={handleVerifyMfaCode}
            />

          <div className="flex justify-end">
            <Button
              onClick={handleSaveChanges}
              variant="contained"
              disabled={!canSaveChanges}
              sx={{
                bgcolor: "#0A2342",
                "&:hover": { bgcolor: "#081B35" },
                textTransform: "uppercase",
                fontWeight: 600,
                px: {xs: 3, sm: 4},
                py: {xs: 1, sm: 1.4},
                borderRadius: "10px",
              }}
            >
              {loading ? "Saving..." : "Save Changes"}
            </Button>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: "100%" }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default AccountSettings;
